"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WorksheetImpl = void 0;
const Contract = require("@tableau/api-external-contract-js");
const api_external_contract_js_1 = require("@tableau/api-external-contract-js");
const DataSource_1 = require("../DataSource");
const LogicalTable_1 = require("../LogicalTable");
const GetDataService_1 = require("../Services/GetDataService");
const ServiceRegistry_1 = require("../Services/ServiceRegistry");
const TableauError_1 = require("../TableauError");
const ErrorHelpers_1 = require("../Utils/ErrorHelpers");
const DataSourceImpl_1 = require("./DataSourceImpl");
const SheetImpl_1 = require("./SheetImpl");
class WorksheetImpl extends SheetImpl_1.SheetImpl {
    constructor(sheetInfoImpl, _registryId, _visualId, _parentDashboardImpl, _parentStoryPointImpl, _backgroundColor = null, _formatting = null) {
        super(sheetInfoImpl, _registryId);
        this._visualId = _visualId;
        this._parentDashboardImpl = _parentDashboardImpl;
        this._parentStoryPointImpl = _parentStoryPointImpl;
        this._backgroundColor = _backgroundColor;
        this._formatting = _formatting;
    }
    get parentDashboard() {
        return this._parentDashboardImpl;
    }
    get parentStoryPoint() {
        return this._parentStoryPointImpl;
    }
    get visualId() {
        return this._visualId;
    }
    getMaxPageRowLimit() {
        return 10000;
    }
    get backgroundColor() {
        return this._backgroundColor;
    }
    get formatting() {
        return this._formatting;
    }
    applyFilterAsync(fieldName, values, updateType, options) {
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(updateType, Contract.FilterUpdateType, 'Contract.FilterUpdateType');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.applyFilterAsync(this.visualId, fieldName, values, updateType, options);
    }
    applyRangeFilterAsync(fieldName, filterOptions) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(fieldName, 'fieldName');
        ErrorHelpers_1.ErrorHelpers.verifyParameter(filterOptions, 'filterOptions');
        if (filterOptions.nullOption) {
            ErrorHelpers_1.ErrorHelpers.verifyEnumValue(filterOptions.nullOption, api_external_contract_js_1.FilterNullOption, 'FilterNullOption');
        }
        else {
            ErrorHelpers_1.ErrorHelpers.verifyRangeParamType(filterOptions.min, filterOptions.max);
        }
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.applyRangeFilterAsync(this.visualId, fieldName, filterOptions);
    }
    applyHierarchicalFilterAsync(fieldName, values, updateType, options) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(fieldName, 'fieldName');
        ErrorHelpers_1.ErrorHelpers.verifyParameter(values, 'values');
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(updateType, Contract.FilterUpdateType, 'Contract.FilterUpdateType');
        if (!Array.isArray(values) && !values.levels) {
            throw new TableauError_1.TableauError(api_external_contract_js_1.ErrorCodes.InvalidParameter, 'values parameter for applyHierarchicalFilterAsync must be an array or contain a levels key');
        }
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.applyHierarchicalFilterAsync(this.visualId, fieldName, values, updateType, options);
    }
    clearFilterAsync(fieldName) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(fieldName, 'fieldName');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.clearFilterAsync(this.visualId, fieldName);
    }
    applyRelativeDateFilterAsync(fieldName, options) {
        ErrorHelpers_1.ErrorHelpers.verifyStringParameter(fieldName, 'fieldName');
        ErrorHelpers_1.ErrorHelpers.verifyParameter(options, 'options');
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(options.periodType, api_external_contract_js_1.PeriodType, 'PeriodType');
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(options.rangeType, api_external_contract_js_1.DateRangeType, 'DateRangeType');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.applyRelativeDateFilterAsync(this.visualId, fieldName, options);
    }
    getDataSourcesAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("data-source-service" /* ServiceNames.DataSourceService */);
        return service.getDataSourcesAsync(this.visualId).then((result) => {
            const dataSchema = result;
            const worksheetDataSourceInfo = dataSchema.worksheetDataSchemaMap[this.name];
            const dataSources = [];
            // First, add the primary datasource.  By convention, it comes first in the returned array.
            const primaryId = worksheetDataSourceInfo.primaryDataSource;
            dataSources.push(this.createDataSourceFromInfo(dataSchema.dataSources[primaryId]));
            // Then, loop through any secondary data sources and add them.
            for (const secondaryId of worksheetDataSourceInfo.referencedDataSourceList) {
                if (secondaryId !== primaryId) {
                    dataSources.push(this.createDataSourceFromInfo(dataSchema.dataSources[secondaryId]));
                }
            }
            return dataSources;
        });
    }
    getFiltersAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.getFiltersAsync(this.visualId);
    }
    getSelectedMarksAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        return service.getSelectedMarksAsync(this.visualId);
    }
    getHighlightedMarksAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        return service.getHighlightedMarksAsync(this.visualId);
    }
    getSummaryDataAsync(options) {
        var _a;
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        options = options || {};
        return service.getUnderlyingDataAsync(this.visualId, GetDataService_1.GetDataType.Summary, !!options.ignoreAliases, !!options.ignoreSelection, true, options.columnsToIncludeById || [], options.maxRows || 0, options.includeDataValuesOption || api_external_contract_js_1.IncludeDataValuesOption.AllValues, (_a = options.applyWorksheetFormatting) !== null && _a !== void 0 ? _a : false);
    }
    getSummaryDataReaderAsync(pageRowCount, options) {
        var _a;
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        options = options || {};
        return service.getSummaryDataReaderAsync(this.visualId, pageRowCount || this.getMaxPageRowLimit(), !!options.ignoreAliases, !!options.ignoreSelection, true, // includeAllColumns (can be overridden by columnsToIncludeById)
        options.columnsToIncludeById || [], options.includeDataValuesOption || api_external_contract_js_1.IncludeDataValuesOption.AllValues, (_a = options.applyWorksheetFormatting) !== null && _a !== void 0 ? _a : false);
    }
    getVisualSpecificationAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("visual-model-service" /* ServiceNames.VisualModel */);
        return service.getVisualSpecificationAsync(this.visualId);
    }
    addMarksCardFieldsAsync(marksCardIndex, encodingType, columns, startIndex) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("visual-model-service" /* ServiceNames.VisualModel */);
        return service.addMarksCardFieldsAsync(this.visualId, marksCardIndex, encodingType, columns, startIndex);
    }
    moveMarksCardFieldAsync(marksCardIndex, fromIndex, toIndex, fieldCount) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("visual-model-service" /* ServiceNames.VisualModel */);
        return service.moveMarksCardFieldAsync(this.visualId, marksCardIndex, fromIndex, toIndex, fieldCount);
    }
    spliceMarksCardFieldsAsync(marksCardIndex, encodingType, startIndex, deleteCount, columns) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("visual-model-service" /* ServiceNames.VisualModel */);
        return service.spliceMarksCardFieldsAsync(this.visualId, marksCardIndex, encodingType, startIndex, deleteCount, columns);
    }
    getSummaryColumnsInfoAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        return service.getSummaryColumnsInfoAsync(this.visualId);
    }
    getUnderlyingDataAsync(options) {
        var _a;
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        options = options || {};
        return service.getUnderlyingDataAsync(this.visualId, GetDataService_1.GetDataType.Underlying, !!options.ignoreAliases, !!options.ignoreSelection, !!options.includeAllColumns, options.columnsToIncludeById || [], options.maxRows || 0, options.includeDataValuesOption || api_external_contract_js_1.IncludeDataValuesOption.AllValues, (_a = options.applyWorksheetFormatting) !== null && _a !== void 0 ? _a : false);
    }
    getUnderlyingTablesAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("data-source-service" /* ServiceNames.DataSourceService */);
        return service.getUnderlyingTablesAsync(this.visualId).then((logicalTableInfos) => {
            return logicalTableInfos.map((logicalTableInfo) => new LogicalTable_1.LogicalTable(logicalTableInfo));
        });
    }
    getUnderlyingTableDataAsync(logicalTableId, options) {
        var _a;
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        options = options || {};
        return service.getUnderlyingTableDataAsync(this.visualId, logicalTableId, !!options.ignoreAliases, !!options.ignoreSelection, !!options.includeAllColumns, options.columnsToIncludeById || [], options.maxRows || 0, options.includeDataValuesOption || api_external_contract_js_1.IncludeDataValuesOption.AllValues, (_a = options.applyWorksheetFormatting) !== null && _a !== void 0 ? _a : false);
    }
    getUnderlyingTableDataReaderAsync(logicalTableId, pageRowCount, options) {
        var _a;
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("get-data-service" /* ServiceNames.GetData */);
        options = options || {};
        return service.getUnderlyingTableDataReaderAsync(this.visualId, logicalTableId, pageRowCount || this.getMaxPageRowLimit(), !!options.ignoreAliases, !!options.ignoreSelection, !!options.includeAllColumns, options.columnsToIncludeById || [], options.includeDataValuesOption || api_external_contract_js_1.IncludeDataValuesOption.AllValues, (_a = options.applyWorksheetFormatting) !== null && _a !== void 0 ? _a : false);
    }
    clearSelectedMarksAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("selection-service" /* ServiceNames.Selection */);
        return service.clearSelectedMarksAsync(this.visualId);
    }
    selectMarksByValueAsync(selections, selectionUpdateType) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(selections, 'fieldName');
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(selectionUpdateType, api_external_contract_js_1.SelectionUpdateType, 'SelectionUpdateType');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("selection-service" /* ServiceNames.Selection */);
        return service.selectMarksByValueAsync(this.visualId, selections, selectionUpdateType);
    }
    selectMarksByIdAsync(selections, selectionUpdateType) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(selections, 'fieldName');
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(selectionUpdateType, api_external_contract_js_1.SelectionUpdateType, 'SelectionUpdateType');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("selection-service" /* ServiceNames.Selection */);
        return service.selectMarksByIdAsync(this.visualId, selections, selectionUpdateType);
    }
    annotateMarkAsync(mark, annotationText) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(mark, 'mark');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("annotation-service" /* ServiceNames.Annotation */);
        return service.annotateMarkAsync(this.visualId, mark, annotationText);
    }
    getAnnotationsAsync() {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("annotation-service" /* ServiceNames.Annotation */);
        return service.getAnnotationsAsync(this.visualId);
    }
    removeAnnotationAsync(annotation) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(annotation, 'annotation');
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("annotation-service" /* ServiceNames.Annotation */);
        return service.removeAnnotationAsync(this.visualId, annotation);
    }
    appendContextMenuAsync(targetMenu, config) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("external-context-menu-service" /* ServiceNames.ExternalContextMenu */);
        return service.appendContextMenuAsync(this.visualId.worksheet, targetMenu, config);
    }
    removeContextMenuAsync(targetMenu, menuItemId) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("external-context-menu-service" /* ServiceNames.ExternalContextMenu */);
        return service.removeContextMenuAsync(this.visualId.worksheet, targetMenu, menuItemId);
    }
    executeContextMenuAsync(targetMenu, menuItemId) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("external-context-menu-service" /* ServiceNames.ExternalContextMenu */);
        return service.executeContextMenuAsync(this.visualId.worksheet, targetMenu, menuItemId);
    }
    renameContextMenuAsync(targetMenu, menuHeader, menuDescription) {
        this.verifyActiveSheet();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("external-context-menu-service" /* ServiceNames.ExternalContextMenu */);
        return service.renameContextMenuAsync(this.visualId.worksheet, targetMenu, menuHeader, menuDescription);
    }
    hoverTupleAsync(hoveredTuple, tooltip, allowHoverActions) {
        if (this.isInsideDashboardExtension()) {
            return Promise.reject(new TableauError_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `hoverTupleAsync is not supported in dashboard extensions`));
        }
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("selection-service" /* ServiceNames.Selection */);
        return service.hoverTupleAsync(this.visualId, hoveredTuple, tooltip, allowHoverActions);
    }
    selectTuplesAsync(selectedTuples, selectOption, tooltip) {
        if (this.isInsideDashboardExtension()) {
            return Promise.reject(new TableauError_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `selectTuplesAsync is not supported in dashboard extensions`));
        }
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("selection-service" /* ServiceNames.Selection */);
        return service.selectTuplesAsync(this.visualId, selectedTuples, selectOption, tooltip);
    }
    getTooltipTextAsync(tupleId) {
        if (this.isInsideDashboardExtension()) {
            return Promise.reject(new TableauError_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `getTooltipTextAsync is not supported in dashboard extensions`));
        }
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("accessibility" /* ServiceNames.Accessibility */);
        return service.getTooltipTextAsync(this.visualId, tupleId);
    }
    leaveMarkNavigationAsync() {
        if (this.isInsideDashboardExtension()) {
            return Promise.reject(new TableauError_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `leaveMarkNavigationAsync is not supported in dashboard extensions`));
        }
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("accessibility" /* ServiceNames.Accessibility */);
        return service.leaveMarkNavigationAsync(this.visualId);
    }
    editAliasesDialogAsync(fieldName) {
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("visual-model-service" /* ServiceNames.VisualModel */);
        return service.editAliasesDialogAsync(fieldName);
    }
    createDataSourceFromInfo(dataSourceInfo) {
        const dataSourceImpl = new DataSourceImpl_1.DataSourceImpl(dataSourceInfo, this._registryId);
        const dataSource = new DataSource_1.DataSource(dataSourceImpl);
        dataSourceImpl.initializeWithPublicInterfaces(dataSource);
        return dataSource;
    }
    verifyActiveSheet() {
        const isRootAndActiveWorksheet = this.active;
        const isInsideActiveDashboard = this.isInsideActiveDashboard();
        const isInsideActiveStoryPoint = this.isInsideActiveStoryPoint();
        if (!isRootAndActiveWorksheet && !isInsideActiveDashboard && !isInsideActiveStoryPoint) {
            throw new TableauError_1.TableauError(api_external_contract_js_1.SharedErrorCodes.NotActiveSheet, 'Operation not allowed on non-active sheet');
        }
    }
    isInsideActiveStoryPoint() {
        return this._parentStoryPointImpl && this._parentStoryPointImpl.active;
    }
    isInsideActiveDashboard() {
        return this._parentDashboardImpl && this._parentDashboardImpl.active;
    }
    isInsideDashboardExtension() {
        return this._parentDashboardImpl !== null;
    }
}
exports.WorksheetImpl = WorksheetImpl;
//# sourceMappingURL=WorksheetImpl.js.map